import { useContext, useEffect, useState } from "react";

import { Coding } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Box, Checkbox, Grid, Group, Stack, Transition, UnstyledButton } from "@mantine/core";

import * as Sentry from "@sentry/react";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { PhysicalExamErrorsContext } from "@/contexts/physicalExamsError";
import { usePhysicalExamType } from "@/contexts/physicalExamType";
import { getValueSetOptions } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { slugify } from "@/utils/strings";

import { PhysicalExamItemProps } from "./types/physical-exams";
import { getCurrentFormValue, getDetailsValues } from "./utils/physical-exams";

export default function PhysicalExamItem({
  name,
  sectionLabel,
  initial,
  label,
  url,
  form,
  disabled,
  hideDetails = false,
}: PhysicalExamItemProps) {
  const [_, section, item] = name.split(".") as [string, string, "normal" | "notNormal"];
  const sectionData = form.values.sections[section];
  const detailsValues = getDetailsValues(sectionData, item);
  const currentFormValue = getCurrentFormValue(sectionData, item);
  const { addPhysicalExamError, removePhysicalExamError } = useContext(PhysicalExamErrorsContext);

  const isNormalDetailsSection = item === "normal";
  const checked = currentFormValue ?? false;

  const examType = usePhysicalExamType();
  const medplum = useMedplum();
  const [isOpen, setIsOpen] = useState(true);
  const [options, setOptions] = useState<Coding[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const options = await getValueSetOptions(medplum, url);
        setOptions(options);
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      }
    })();
  }, [url, medplum]);

  const handleCheckboxChange = (e: string[] | boolean, targetName: string) => {
    form.setFieldValue(targetName, e);
    if (isNormalDetailsSection) {
      const formValidation = form.validate();

      // Check if the current section has validation errors
      const currentSectionHasErrors =
        formValidation.errors &&
        Object.keys(formValidation.errors).some(
          (key) => key.startsWith(`sections.${section}.`) && key.includes("normalDetails"),
        );

      if (currentSectionHasErrors) {
        addPhysicalExamError(sectionLabel);
      } else {
        removePhysicalExamError(sectionLabel);
      }
      return;
    }
  };

  const handleClickProvideMoreDetail = (_e: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen(!isOpen);
  };

  const IconComponent = isOpen ? IconChevronUp : IconChevronDown;

  const showMore = !disabled && !hideDetails && checked;
  const listFieldName = `sections.${section}.${item}Details`;

  return (
    <Box aria-label={`${sectionLabel} ${label} checkbox`}>
      <Grid mt="lg" justify="flex-start">
        <Grid.Col span={{ base: 12, sm: 3 }}>
          <Checkbox
            name={name}
            label={label}
            disabled={disabled}
            data-testid={
              examType
                ? `physical-exam-${examType}-${item === "normal" ? "normal" : "not-normal"}`
                : undefined
            }
            {...form.getInputProps(name, { type: "checkbox" })}
            onChange={(e) => handleCheckboxChange(e.target.checked, name)}
          />
        </Grid.Col>
        {showMore && (
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <UnstyledButton
              aria-label="Provide more detail"
              onClick={handleClickProvideMoreDetail}
              style={{ display: "flex", alignItems: "center" }}
              data-testid={examType ? `physical-exam-${examType}-${item}-more-detail` : undefined}
            >
              <Group gap="xs">
                <TextWrapper variant="bold">Provide more detail</TextWrapper>
                {isNormalDetailsSection && (
                  <TextWrapper c="red.6" variant="default">
                    *
                  </TextWrapper>
                )}
              </Group>
              <IconComponent size={16} style={{ marginLeft: "var(--mantine-spacing-xs)" }} />
            </UnstyledButton>
          </Grid.Col>
        )}
      </Grid>
      <Transition
        duration={200}
        timingFunction="ease"
        transition="fade-down"
        mounted={showMore && isOpen}
      >
        {(style) => (
          <Grid mt="md" style={style}>
            <Grid.Col span={{ base: 12, sm: 9 }} offset={{ base: 0, sm: 3 }}>
              <Checkbox.Group
                value={detailsValues}
                {...form.getInputProps(listFieldName, { type: "checkbox" })}
                styles={{ error: { marginTop: 10 } }}
                onChange={(e) => handleCheckboxChange(e, listFieldName)}
              >
                <Stack gap="lg">
                  {options.map((option) => (
                    <Checkbox
                      key={option.code}
                      value={option.code}
                      label={option.display}
                      data-testid={
                        examType && option.display
                          ? `physical-exam-${examType}-detail-${slugify(option.display)}`
                          : undefined
                      }
                    />
                  ))}
                </Stack>
              </Checkbox.Group>
            </Grid.Col>
          </Grid>
        )}
      </Transition>
    </Box>
  );
}
